import { safeFromUnixTime } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { safelyRender } from '@liveauctioneers/caterwaul-components/lib/utils/safelyRender';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { differenceInCalendarDays } from 'date-fns';
import * as React from 'react';
import styled from 'styled-components';

import ActionButtons from '../ActionButtons/ActionButtons';
import { Folder } from '../Folders/types/Folder';
import ClosedConversationPrompt from './components/ClosedConversationPrompt';
import Message from './components/Message/Message';
import RichMessageEditor from './components/RichMessageEditor/RichMessageEditor';
import { MessageType } from './types/Message';

export type MessengerProps = {
    /** Index of the currently active folder in `folders` */
    readonly activeFolder: number | null;
    /** Copy for "Archive" button */
    readonly archiveLabel: React.ReactNode;
    /** Whether catalog is online or live */
    readonly catalogIsOnlineOrLive?: boolean;
    /** List of folders for the current user */
    readonly folders: Folder[];
    /** Name of auction house involved in conversations */
    readonly houseName: string;
    /** Copy for "Mark Read" button */
    readonly isHouse?: boolean;
    readonly markReadLabel: React.ReactNode;
    /** Copy for "Mark Unread" button */
    readonly markUnreadLabel: React.ReactNode;
    /** List of messages in this conversation */
    readonly messages: MessageType[];
    /** Copy for "Move" button */
    readonly moveLabel: React.ReactNode;
    /** Function to add attachment(s) to a draft, a draft will be created if it does not exist */
    readonly onAddAttachment: (message: string, attachedFiles: any[]) => void;
    /** Callback function for archiving conversation */
    readonly onArchive: () => void;
    /** Callback function when clicking the "back to inbox" button */
    readonly onBack: (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
    /** Function to delete attachment from message draft */
    readonly onDeleteAttachment: (attachmentName: string) => void;
    /** Callback function to mark a conversation as read */
    readonly onMarkRead: () => void;
    /** Callback function to mark a conversation as unread */
    readonly onMarkUnread: () => void;
    /** Callback function to move a conversation to a different folder */
    readonly onMove: (folder: Folder) => void;
    /** Callback function to send a message */
    readonly onSendMessage: (message: string) => void;
    /** Subject line of conversation */
    readonly subject: string;
    readonly testid?: string;
};

const Messenger = ({
    activeFolder,
    archiveLabel,
    catalogIsOnlineOrLive,
    folders = [],
    houseName,
    isHouse,
    markReadLabel,
    markUnreadLabel,
    messages,
    moveLabel,
    onAddAttachment,
    onArchive,
    onBack,
    onDeleteAttachment,
    onMarkRead,
    onMarkUnread,
    onMove,
    onSendMessage,
    subject,
    testid = 'messenger',
}: MessengerProps) => {
    const messageListRef = React.useRef<HTMLUListElement>(null);
    const [readStatus, setReadStatus] = React.useState<'neither' | 'read' | 'unread' | 'both'>('unread');

    const onMarkReadToggle = () => {
        setReadStatus('unread');
        onMarkRead();
    };

    const onMarkUnreadToggle = () => {
        setReadStatus('read');
        onMarkUnread();
    };

    // Scroll to bottom of message list when number of messages changes
    React.useEffect(() => {
        if (messageListRef.current) {
            const { clientHeight, scrollHeight } = messageListRef.current;
            const maxScrollTop = scrollHeight - clientHeight;

            messageListRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
        }
    }, [messages.length]);

    const latestMessageSent = Math.max.apply(
        Math,
        messages.map((m: MessageType) => m.tsSent)
    );
    const threadOlderThan30Days = differenceInCalendarDays(new Date(), safeFromUnixTime(latestMessageSent)) > 30;
    const showClosedConversation = threadOlderThan30Days && !catalogIsOnlineOrLive;

    return (
        <MessengerContainer data-testid={testid}>
            <StyledActionButtons
                activeFolder={activeFolder}
                archiveLabel={archiveLabel}
                back={onBack}
                enabled
                folders={folders}
                isRead={readStatus}
                markReadLabel={markReadLabel}
                markUnreadLabel={markUnreadLabel}
                moveLabel={moveLabel}
                onArchive={onArchive}
                onMarkRead={onMarkReadToggle}
                onMarkUnread={onMarkUnreadToggle}
                onMove={onMove}
            />
            <SubjectContainer as="h5">{safelyRender({ html: subject, wrapper: 'span' })}</SubjectContainer>
            <MessageList ref={messageListRef}>
                {messages.map((currMessage: MessageType) => (
                    <React.Fragment key={currMessage.messageId}>
                        <Message message={currMessage} />
                    </React.Fragment>
                ))}
            </MessageList>
            <InteractionContainerDiv>
                {showClosedConversation ? (
                    <ClosedConversationPrompt
                        houseName={houseName}
                        isHouse={isHouse}
                        onBack={onBack}
                    />
                ) : (
                    <RichMessageEditor
                        onAddAttachment={onAddAttachment}
                        onDeleteAttachment={onDeleteAttachment}
                        onSendMessage={onSendMessage}
                    />
                )}
            </InteractionContainerDiv>
        </MessengerContainer>
    );
};

export default Messenger;

const InteractionContainerDiv = styled.div`
    @media print {
        display: none;
    }
`;

const MessageList = styled.ul`
    list-style-type: none;
    padding: 0 0 20px 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

const SubjectContainer = styled(H5)`
    margin: 15px 0 0 0;
    padding: 12px 0 16px 53px;
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};
`;

const MessengerContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
`;

const StyledActionButtons = styled(ActionButtons)`
    margin-top: 1px;
`;
